import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { paymentsApi } from '@/api/payments';

export interface BankAccount {
  id: string;
  accountName: string;
  sortCode: string;
  accountNumber: string;
  isDefault: boolean;
  stripe_account_id?: string;
  stripe_onboarding_completed?: boolean;
}

export type PaymentHistory = {
  id: string;
  date: string;
  title: string;
  amount: number;
  jobAmount: number;
  status: 'Completed' | 'Pending';
  receiptId: string;
  paymentDate: string | null;
  adminPaid: boolean;
};

export const usePayments = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Fetch payment methods
  const fetchPaymentMethods = async () => {
    if (!user?.id) return [];

    const data = await paymentsApi.getFilmmakerBanking(user.id);
    
    return data.map((item) => ({
      id: item.banking_id,
      accountName: item.account_name,
      sortCode:
        item.sort_code.length === 6
          ? item.sort_code.replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3')
          : item.sort_code, // Format for display
      accountNumber: item.account_number,
      isDefault: item.is_default || false,
      stripe_account_id: item.stripe_account_id,
      stripe_onboarding_completed: item.stripe_onboarding_completed || false,
    }));
  };

  // Fetch real payment history from the database
  const fetchPaymentHistory = async () => {
    if (!user?.id) return [];

    const data = await paymentsApi.getFilmmakerPaymentHistory(user.id);

    // Format the payment data
    return data.map((payment) => {
      // Extract job title from the related data if available
      const jobTitle = payment.bid_project?.bid?.job?.title || 'Payment';

      // Format the payment date
      const paymentDate =
        payment.admin_payment_date || payment.client_payment_date;
      const formattedDate = paymentDate
        ? format(new Date(paymentDate), 'MMM dd, yyyy')
        : 'Pending';

      // Determine payment status
      const status = payment.payout_status ? 'Completed' : 'Pending';

      return {
        id: payment.payment_id,
        title: jobTitle,
        date: formattedDate,
        jobAmount: payment.amount || 0,
        amount: payment.payment_amount || 0,
        status: status,
        receiptId: `REC-${payment.payment_id.substring(0, 8)}`,
        paymentDate: paymentDate,
        adminPaid: !!payment.admin_payment_date,
      };
    });
  };

  // Fetch wallet balance - mock for now
  const fetchWalletBalance = async () => {
    return 2550; // This would be replaced with real API call
  };

  // Queries
  const { 
    data: paymentMethods = [], 
    isLoading: isPaymentMethodsLoading,
    refetch: refetchPaymentMethods 
  } = useQuery({
      queryKey: ['paymentMethods', user?.id],
      queryFn: fetchPaymentMethods,
      enabled: !!user?.id,
      staleTime: 1000 * 60 * 30, // 30 minutes - data is considered fresh
      gcTime: 1000 * 60 * 60, // 60 minutes - data stays in cache
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false, // Don't refetch if data exists in cache
      refetchOnReconnect: false, // Don't refetch on reconnect
    });

  const { 
    data: paymentHistory = [], 
    isLoading: isPaymentHistoryLoading,
    refetch: refetchPaymentHistory 
  } = useQuery({
      queryKey: ['paymentHistory', user?.id],
      queryFn: fetchPaymentHistory,
      enabled: !!user?.id,
      staleTime: 1000 * 60 * 30, // 30 minutes - data is considered fresh
      gcTime: 1000 * 60 * 60, // 60 minutes - data stays in cache
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false, // Don't refetch if data exists in cache
      refetchOnReconnect: false, // Don't refetch on reconnect
    });

  // Calculate total earnings from payment history
  const totalEarnings = React.useMemo(() => {
    if (!paymentHistory || paymentHistory.length === 0) return '£0';
    
    const completed = paymentHistory.filter(
      (p) => p.status === 'Completed'
    );
    const sum = completed.reduce((acc, p) => acc + (p.amount || 0), 0);
    
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(sum);
  }, [paymentHistory]);
  
  const isTotalEarningsLoading = isPaymentHistoryLoading;

  const { 
    data: walletBalance = 0, 
    isLoading: isWalletBalanceLoading,
    refetch: refetchWalletBalance
  } = useQuery({
      queryKey: ['walletBalance', user?.id],
      queryFn: fetchWalletBalance,
      enabled: !!user?.id,
      staleTime: 1000 * 60 * 30, // 30 minutes - data is considered fresh
      gcTime: 1000 * 60 * 60, // 60 minutes - data stays in cache
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false, // Don't refetch if data exists in cache
      refetchOnReconnect: false, // Don't refetch on reconnect
    });

  // Mutation to add payment method
  const addPaymentMethod = useMutation({
    mutationFn: async (newBankAccount: Omit<BankAccount, 'id'>) => {
      if (!user?.id) throw new Error('User ID not found');

      // Strip dashes from sort code for database storage
      const cleanSortCode = newBankAccount.sortCode.replace(/[\s-]/g, '');

      const data = await paymentsApi.createBankAccount(user.id, {
        account_name: newBankAccount.accountName,
        sort_code: cleanSortCode,
        account_number: newBankAccount.accountNumber,
        is_default: newBankAccount.isDefault,
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', user?.id] });
    },
  });

  // Mutation to set default payment method
  const setDefaultPaymentMethod = useMutation({
    mutationFn: async (bankingId: string) => {
      if (!user?.id) throw new Error('User ID not found');

      const data = await paymentsApi.updateBankAccount(user.id, bankingId, {
        is_default: true,
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', user?.id] });
    },
  });

  // Mutation to delete payment method
  const deletePaymentMethod = useMutation({
    mutationFn: async (bankingId: string) => {
      if (!user?.id) throw new Error('User ID not found');

      await paymentsApi.deleteBankAccount(user.id, bankingId);
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', user?.id] });
    },
  });

  const isLoading =
    isPaymentMethodsLoading ||
    isPaymentHistoryLoading ||
    isWalletBalanceLoading ||
    isTotalEarningsLoading;

  return {
    paymentMethods,
    paymentHistory,
    walletBalance,
    isLoading,
    addPaymentMethod,
    setDefaultPaymentMethod,
    deletePaymentMethod,
    totalEarnings,
    isTotalEarningsLoading,
    refetchPaymentMethods,
    refetchPaymentHistory,
    refetchWalletBalance,
  };
};
